<script lang="ts" setup>
  import { CartLayout, CartWithSummaryLayout } from '@storefront/components';
  import { useAuthenticated, useCountriesManager } from '@othercommerce/foundation';
  import { useVerifiedCart } from '@storefront/storefront';
  import { computed } from 'vue';
  import { type Dictionary } from '@othercommerce/domain';

  defineOptions({
    layout: [CartLayout, CartWithSummaryLayout],
    inheritAttrs: false,
  });

  interface Props {
    isMedicalProvider: boolean;
    hasMedicinalProducts: boolean;
    billingTypes: Dictionary;
    regions: Record<string, Dictionary>,
  }

  const props = defineProps<Props>();
  const authenticated = useAuthenticated();
  const cart = useVerifiedCart();
  const countriesManager = useCountriesManager();

  const billingData = computed(() => {
    return {
      billingType: cart.value.billingType,
      billingCustom: cart.value.billingCustom,
      billingVatId: cart.value.billingVatId,
      billingFirstName: cart.value.billingFirstName,
      billingLastName: cart.value.billingLastName,
      billingCompany: cart.value.billingCompany,
      billingStreet: cart.value.billingStreet,
      billingBuildingNr: cart.value.billingBuildingNr,
      billingApartmentNr: cart.value.billingApartmentNr,
      billingFirstLine: cart.value.billingFirstLine,
      billingSecondLine: cart.value.billingSecondLine,
      billingPostcode: cart.value.billingPostcode,
      billingCity: cart.value.billingCity,
      billingRegion: cart.value.billingRegion,
      billingEmail: cart.value.billingEmail,
      billingPhone: cart.value.billingPhone,
    };
  });

  const implicit = computed(() => {
    return countriesManager.isImplicit(cart.value.country.code);
  });

  const billingFillable = computed(() => {
    if (authenticated.value) {
      let erpId = authenticated.value.erpId;

      if (erpId !== null) {
        return false;
      }
    }

    return true;
  });

  const countryRegions = computed(() => {
    if (!cart.value.country.code || !props.regions || props.isMedicalProvider) return null;

    return props.regions[cart.value.country.code];
  });
</script>
<template>
  <div class="billing-data-form">
    <FormController v-slot="{ data, processing, submit }" :action="$route('web.cart.billing.address.update')" :data="billingData" method="PATCH">
      <template v-if="billingTypes.length > 1">
        <SfFormRadio :label="$t('SHOP.CART.LABEL.BILLING_TYPE')" :options="billingTypes" :readonly="!billingFillable" name="billingType" />
      </template>

      <div class="billing-data-form__info">
        <SfIconInfo aria-hidden="true" class="billing-data-form__icon" />
        <p>{{ $t('SHOP.CART.MESSAGE.INVOICE_NOTICE') }}</p>
      </div>

      <template v-if="data.billingType === 'PERSONAL'">
        <SfFormSwitch :label="$t('SHOP.CART.LABEL.BILLING_CUSTOM')" name="billingCustom" />
      </template>

      <template v-if="data.billingType === 'COMPANY' || data.billingCustom">
        <div class="billing-data-form__form">
          <UiButtonGroup v-if="authenticated && billingFillable && !hasMedicinalProducts">
            <SfButton :href="$route('web.cart.billing.address.choose')" primary small>
              <template #before>
                <SfIconBook aria-hidden="true" />
              </template>
              <template #default>
                {{ $t('SHOP.CART.ACTION.FILL_FROM_SAVED_ADDRESSES') }}
              </template>
            </SfButton>
          </UiButtonGroup>

          <SfFormInput v-if="data.billingType === 'COMPANY'" :label="$t('SHOP.COMMON.LABEL.VAT_ID')" :readonly="!billingFillable || isMedicalProvider" name="billingVatId" required type="text" />
          <SfFormInput :label="$t('SHOP.COMMON.LABEL.FIRSTNAME')" :readonly="!billingFillable" name="billingFirstName" required type="text" />
          <SfFormInput :label="$t('SHOP.COMMON.LABEL.LASTNAME')" :readonly="!billingFillable" name="billingLastName" required type="text" />
          <SfFormInput v-if="data.billingType !== 'PERSONAL'" :label="$t('SHOP.COMMON.LABEL.COMPANY_NAME')" :readonly="!billingFillable" name="billingCompany" required type="text" />

          <template v-if="implicit">
            <div class="address-line">
              <div class="address-street">
                <SfFormInput :label="$t('SHOP.COMMON.LABEL.STREET')" :readonly="!billingFillable" name="billingStreet" required type="text" />
              </div>
              <div class="address-building-nr">
                <SfFormInput :label="$t('SHOP.COMMON.LABEL.BUILDING_NR')" :readonly="!billingFillable" name="billingBuildingNr" required type="text" />
              </div>
              <div class="address-apartment-nr">
                <SfFormInput :label="$t('SHOP.COMMON.LABEL.APARTMENT_NR')" :readonly="!billingFillable" name="billingApartmentNr" type="text" />
              </div>
            </div>
          </template>

          <template v-else>
            <SfFormInput :label="$t('SHOP.COMMON.LABEL.FIRST_LINE')" :readonly="!billingFillable" name="billingFirstLine" required type="text" />
            <SfFormInput :label="$t('SHOP.COMMON.LABEL.SECOND_LINE')" :readonly="!billingFillable" name="billingSecondLine" type="text" />
          </template>

          <div class="address-line">
            <div class="address-postcode">
              <SfFormInput :label="$t('SHOP.COMMON.LABEL.POSTCODE')" :readonly="!billingFillable" name="billingPostcode" required type="text" />
            </div>
            <div class="address-city">
              <SfFormInput :label="$t('SHOP.COMMON.LABEL.CITY')" :readonly="!billingFillable" name="billingCity" required type="text" />
            </div>
          </div>

          <SfFormSelect v-if="countryRegions" :label="$t('SHOP.COMMON.LABEL.REGION')" :options="countryRegions" :readonly="!billingFillable" name="billingRegion" />
          <SfFormInput v-else :label="$t('SHOP.COMMON.LABEL.REGION')" :readonly="!billingFillable" name="billingRegion" type="text" />

          <SfFormInput :label="$t('SHOP.COMMON.LABEL.BILLING_EMAIL')" name="billingEmail" required type="email" />
          <SfFormInput :label="$t('SHOP.COMMON.LABEL.BILLING_PHONE')" name="billingPhone" type="tel" />
        </div>
      </template>
      <UiButtonGroup>
        <SfButton :loading="processing" primary @click="submit">
          {{ $t('SHOP.ACTION.CONTINUE') }}
        </SfButton>
      </UiButtonGroup>
    </FormController>
    <RouterView />
  </div>
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .billing-data-form {
    display: flex;
    flex-direction: column;
    gap: 2rem;

    &__address {
      font-style: normal;
      font-size: 0.875rem;
    }

    &__info {
      display: flex;
      gap: 0.5rem;

      p {
        font-size: 0.875rem;
        line-height: 1.25rem;
        color: var(--color-secondary);
      }
    }

    &__icon {
      font-size: 1.25rem;
      line-height: 1.25rem;
      vertical-align: middle;
    }

    &__form {
      display: flex;
      flex-direction: column;
      max-width: 672px;
      gap: var(--form-gap-y) var(--form-gap-x);

      .address-line {
        display: flex;
        flex-direction: row;
        gap: var(--form-gap-y) var(--form-gap-x);
      }

      .address-street {
        flex: 1 1 auto;
      }

      .address-building-nr,
      .address-apartment-nr {
        flex: 0 0 128px;
      }

      .address-postcode {
        flex: 0 0 208px;
      }

      .address-city {
        flex: 1 1 auto;
      }
    }

    &__medical-label {
      display: inline-block;
      color: var(--sf-input-label-color);
      font-size: var(--sf-input-label-font-size);
    }

    @include media.query(xl) {
      gap: 1rem;

      @include media.query(sm) {
        .address-line {
          flex-wrap: wrap;
        }
        .address-postcode,
        .address-apartment-nr,
        .address-building-nr,
        .address-street {
          flex-basis: 100%;
        }
      }
    }
  }
</style>
